import {
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  updatePassword,
} from 'aws-amplify/auth';
import { authFlowType } from './amplify';

/**
 * The authentication surface the rest of the app uses.
 *
 * Everything Amplify-shaped stays behind this module, so screens never import
 * `aws-amplify` directly. That is what makes the auth provider replaceable:
 * there is exactly one place to change.
 */

export type SignInOutcome =
  | { status: 'signed-in' }
  /** Cognito requires a new password before the account can be used. */
  | { status: 'new-password-required' };

export class AuthError extends Error {
  constructor(
    message: string,
    /** Cognito's error name, for mapping to a translated message. */
    readonly code: string,
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

const toAuthError = (error: unknown): AuthError => {
  if (error instanceof Error) {
    return new AuthError(error.message, error.name);
  }

  return new AuthError(String(error), 'UnknownError');
};

export const signIn = async (username: string, password: string): Promise<SignInOutcome> => {
  try {
    const { isSignedIn, nextStep } = await amplifySignIn({
      username,
      password,
      options: { authFlowType: authFlowType() },
    });

    if (isSignedIn) {
      return { status: 'signed-in' };
    }

    if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
      return { status: 'new-password-required' };
    }

    // MFA and the other challenge steps are not configured on this pool. Failing
    // loudly beats a login screen that silently does nothing on an unhandled
    // challenge.
    throw new AuthError(`Unsupported sign-in step: ${nextStep.signInStep}`, nextStep.signInStep);
  } catch (error) {
    throw toAuthError(error);
  }
};

/** Completes the forced-password-change challenge left by {@link signIn}. */
export const completeNewPassword = async (newPassword: string): Promise<void> => {
  try {
    const { isSignedIn } = await confirmSignIn({ challengeResponse: newPassword });

    if (!isSignedIn) {
      throw new AuthError('Password was set but the session did not open', 'NotSignedIn');
    }
  } catch (error) {
    throw toAuthError(error);
  }
};

export const changePassword = async (oldPassword: string, newPassword: string): Promise<void> => {
  try {
    await updatePassword({ oldPassword, newPassword });
  } catch (error) {
    throw toAuthError(error);
  }
};

export const signOut = async (): Promise<void> => {
  await amplifySignOut();
};

/**
 * The current access token, refreshed if needed, or `undefined` if not signed in.
 *
 * `fetchAuthSession` returns a valid token or exchanges the refresh token for
 * one. This is the only place a token is read, and it is read per request rather
 * than cached in a module — which is what makes expiry a non-event instead of
 * §4.4's silent logout.
 */
export const accessToken = async (): Promise<string | undefined> => {
  try {
    const { tokens } = await fetchAuthSession();

    return tokens?.accessToken?.toString();
  } catch {
    return undefined;
  }
};

export const hasSession = async (): Promise<boolean> => Boolean(await accessToken());

/**
 * A human label for the signed-in user.
 *
 * Not from `/v1/me`, deliberately. That endpoint reports what the *access token*
 * says, and the token's `username` claim is a UUID under `cognito-local` (real
 * Cognito puts the username there). The user attributes carry the email, which
 * is what the person typed to sign in and what they expect to see in the header.
 *
 * `/v1/me`'s identity remains the authority for authorization and for the audit
 * trail; this is presentation only.
 */
export const displayName = async (): Promise<string | undefined> => {
  try {
    const attributes = await fetchUserAttributes();

    return attributes.email ?? attributes.preferred_username;
  } catch {
    return undefined;
  }
};
